import { CoursePagePremiumLoader } from '@/components/course/CoursePagePremiumLoader'
import { filterCourses, DEFAULT_INSTRUMENT_PICK } from '@/components/courses/courseCatalogFilters'
import { CoursesCatalogCard } from '@/components/courses/CoursesCatalogCard'
import { CoursesCatalogRow } from '@/components/courses/CoursesCatalogRow'
import { useCoursesHomeFilters } from '@/components/courses/CoursesHomeToolbar'
import { CoursesHubSearch, type CoursesHubSearchState } from '@/components/courses/CoursesHubSearch'
import { CoursesPageAnnouncementModal } from '@/components/courses/CoursesPageAnnouncementModal'
import { CoursesPersonalHub } from '@/components/courses/CoursesPersonalHub'
import { FindDashboardSurveyModal } from '@/components/courses/FindDashboardSurveyModal'
import { PromoCtaBanner } from '@/components/courses/PromoCtaBanner'
import { SubscriptionHubCtas } from '@/components/subscription/SubscriptionHubCtas'
import { HelpScoutBeacon } from '@/components/HelpScoutBeacon'
import { BodyText, CaptionText, ErrorDisplay, SectionTitle } from '@/components/ui'
import { DashboardViewModeToggle } from '@/components/ui/DashboardViewModeToggle'
import { purchasedCoursesCategoryQueryOptions } from '@/hooks/purchasedCoursesQueries'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useEffectiveMinStatus } from '@/hooks/useDraftViewer'
import { useSearchTracking } from '@/hooks/useSearchTracking'
import { useTrialCourseCta } from '@/hooks/useTrialCourseCta'
import { MIN_SEARCH_QUERY_LENGTH } from '@/components/search/globalSearchConstants'
import { useAuth } from '@/lib/AuthContext'
import { BRAND_COLORS } from '@/lib/brandColors'
import { CourseSkillLevel, LessonStatus } from '@/lib/constants'
import { courseThumbnailUri } from '@/lib/courseThumbnail'
import {
  DASHBOARD_GRID_GAP,
} from '@/lib/dashboardToolbarUi'
import { derivePersonalHubModel } from '@/lib/derivePersonalHubModel'
import { courseGridLayout } from '@/lib/courseGridLayout'
import { chunkIntoRows } from '@/lib/gridRows'
import { CATALOG_STALE_TIME, queryKeys, USER_DATA_STALE_TIME } from '@/lib/queryClient'
import { useCurrentUrl } from '@/lib/useCurrentPath'
import {
  CONTENT_MAX_WIDTH,
  useAutoHideScrollbar,
  useCatalogProgressBarHeight,
  useIsMobile,
  useStableWindowWidth,
} from '@/lib/useDeviceType'
import { dashboardSlugFromHomeUrl, resolveUserHomeUrl } from '@/lib/userHomeUrl'
import { mixpanelService } from '@/services/mixpanel.service'
import {
  COURSE_CATALOG_BROWSE_LIMIT,
  catalogRowToCourse,
  searchCourseCatalog,
} from '@/supabase/services/courseCatalogSearch'
import { getCourseBySlug } from '@/supabase/services/courses'
import {
  fetchContinueLessonsForDashboard,
  fetchUserCourseProgress,
} from '@/supabase/services/optimizedCourse'
import { Course } from '@/types'
import { useScrollToTop } from '@react-navigation/native'
import { useQuery } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FlatList, Platform, type ListRenderItem } from 'react-native'
import { Button, XStack, YStack } from 'tamagui'

const CONTAINER_PAD_X = 16
const GRID_GAP = DASHBOARD_GRID_GAP

const CourseRowSeparator = () => <YStack h={GRID_GAP} />

export default function CoursesListScreen() {
  const { user, preferenceMetadata, membershipTier, membershipLoading } = useAuth()
  const trialCta = useTrialCourseCta(user?.id)
  const minStatus = useEffectiveMinStatus()
  const { fullPath } = useCurrentUrl()
  const [searchQuery, setSearchQuery] = useState('')
  const [hubSearch, setHubSearch] = useState<CoursesHubSearchState | null>(null)
  const [surveyOpen, setSurveyOpen] = useState(false)
  const isSearchActive = searchQuery.trim().length > 0
  const { scrollbarStyle, onScroll: handleCatalogScroll } = useAutoHideScrollbar()
  const listRef = useRef<FlatList<Course[]>>(null)

  // Standard "tap the active tab → scroll to top" behaviour. Listens for the
  // tabPress event emitted by the bottom bar's Home button.
  useScrollToTop(listRef)

  const isMobile = useIsMobile()
  const stableWidth = useStableWindowWidth()
  const progressBarHeight = useCatalogProgressBarHeight()
  const layout = useMemo(() => {
    const inner = Math.min(CONTENT_MAX_WIDTH, stableWidth) - CONTAINER_PAD_X * 2
    return courseGridLayout(inner)
  }, [stableWidth])
  const { cardWidth, numColumns: gridColumns, justify } = layout
  const [viewMode, setViewMode] = usePersistedState<'grid' | 'list'>(
    'courses_view_mode',
    'grid',
  )
  const pageWidth = useMemo(
    () => Math.min(CONTENT_MAX_WIDTH, stableWidth),
    [stableWidth],
  )
  const gridInnerWidth = pageWidth - CONTAINER_PAD_X * 2

  const { tierPick, setTierPick, instrumentPick, setInstrumentPick } = useCoursesHomeFilters()

  const {
    data: catalogRows = [],
    isLoading: loading,
    error,
    refetch: loadCourses,
  } = useQuery({
    // Skill levels + instruments are both filtered client-side, so filter
    // changes never refetch — the full catalog is loaded once here.
    queryKey: queryKeys.courses.catalogHub(user?.id ?? null, minStatus),
    queryFn: () =>
      searchCourseCatalog(
        '',
        COURSE_CATALOG_BROWSE_LIMIT,
        user?.id,
        null,
        minStatus,
      ),
    staleTime: CATALOG_STALE_TIME,
  })

  const catalogCourses = useMemo(
    () => catalogRows.map(catalogRowToCourse),
    [catalogRows],
  )

  const homeUrl = useMemo(
    () =>
      resolveUserHomeUrl(
        preferenceMetadata ?? undefined,
        membershipTier,
        membershipLoading,
      ),
    [preferenceMetadata, membershipTier, membershipLoading],
  )

  const dashboardSlug = dashboardSlugFromHomeUrl(homeUrl)
  const { data: dashboardCourse } = useQuery({
    queryKey: queryKeys.course.bySlug(dashboardSlug ?? ''),
    queryFn: () => getCourseBySlug(dashboardSlug as string),
    enabled: !!dashboardSlug,
    staleTime: CATALOG_STALE_TIME,
  })

  const canUseHomeDashboard =
    !!user && !user.is_anonymous && !membershipLoading

  const showDashboardCard = canUseHomeDashboard && !!dashboardSlug

  const openSurvey = useCallback(() => {
    if (canUseHomeDashboard) setSurveyOpen(true)
  }, [canUseHomeDashboard])

  const { data: purchasedCategory } = useQuery(
    purchasedCoursesCategoryQueryOptions(user?.id ?? null),
  )
  const purchasedCourses = purchasedCategory?.courses ?? []

  const { data: continueLessons = [] } = useQuery({
    queryKey: queryKeys.purchasedCourses.continue(user?.id ?? null),
    queryFn: () =>
      fetchContinueLessonsForDashboard(
        user?.id ?? null,
        undefined,
        [],
        LessonStatus.Published,
        [],
        true,
        true,
      ),
    staleTime: USER_DATA_STALE_TIME,
    enabled: !!user?.id,
    gcTime: 1 * 60 * 1000,
  })

  const filteredCourses = useMemo(
    () => filterCourses(catalogCourses, instrumentPick, tierPick),
    [catalogCourses, instrumentPick, tierPick],
  )

  const courseIds = useMemo(() => catalogCourses.map((c) => c.id), [catalogCourses])

  const { data: progressByCourseId = {} } = useQuery({
    queryKey: queryKeys.userLessons.courseProgress(user?.id ?? null, courseIds),
    queryFn: () => fetchUserCourseProgress(user?.id ?? null, courseIds),
    staleTime: CATALOG_STALE_TIME,
    enabled: !!user?.id && courseIds.length > 0,
  })

  const personalHub = useMemo(
    () =>
      derivePersonalHubModel({
        continueLesson: continueLessons[0],
        dashboardCourse: showDashboardCard ? dashboardCourse : undefined,
        dashboardHref: showDashboardCard ? homeUrl : undefined,
        purchasedCount: purchasedCourses.length,
        allowDashboardSetup: canUseHomeDashboard,
      }),
    [
      continueLessons,
      showDashboardCard,
      dashboardCourse,
      homeUrl,
      purchasedCourses.length,
      canUseHomeDashboard,
    ],
  )

  const displayedCourses = useMemo(
    () => (isSearchActive ? hubSearch?.results ?? [] : filteredCourses),
    [isSearchActive, hubSearch, filteredCourses],
  )

  const thumbnailUris = useMemo(
    () => Object.fromEntries(displayedCourses.map((c) => [c.id, courseThumbnailUri(c)])),
    [displayedCourses],
  )

  const hasActiveFilters =
    Object.values(tierPick).some(Boolean) ||
    Object.values(instrumentPick).some(Boolean)

  useEffect(() => {
    mixpanelService.trackCoursesRootVisit(user, fullPath)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchTracking = useSearchTracking({ scope: 'courses_hub' })

  useEffect(() => {
    if (!hubSearch) return
    if (hubSearch.query.length < MIN_SEARCH_QUERY_LENGTH) return
    searchTracking.onResultsSettled(
      hubSearch.query,
      hubSearch.results.map((c) => ({
        id: c.id,
        type: 'course' as const,
        slug: c.slug,
        title: c.title,
      })),
    )
  }, [hubSearch, searchTracking])

  const handleSearchResultPress = useCallback(
    (course: Course) => {
      if (!hubSearch) return
      const position =
        displayedCourses.findIndex((c) => c.id === course.id) + 1
      searchTracking.trackResultClick(
        hubSearch.query,
        { id: course.id, type: 'course', slug: course.slug, title: course.title },
        position,
      )
    },
    [hubSearch, displayedCourses, searchTracking],
  )

  const clearFilters = useCallback(() => {
    setTierPick({
      [CourseSkillLevel.Beginner]: false,
      [CourseSkillLevel.Intermediate]: false,
      [CourseSkillLevel.Advanced]: false,
    })
    setInstrumentPick({ ...DEFAULT_INSTRUMENT_PICK })
  }, [setTierPick, setInstrumentPick])

  const listHeader = useMemo(
    () => (
      <YStack pt="$3" width={pageWidth} maxWidth={CONTENT_MAX_WIDTH} px={CONTAINER_PAD_X}>
        {!isSearchActive ? (
          <>
            {/* An enabled First Steps trial CTA replaces the promo banner for its audience. */}
            {!trialCta.active ? <PromoCtaBanner userId={user?.id} /> : null}
            <SubscriptionHubCtas userId={user?.id} />
          </>
        ) : null}
        {!isSearchActive && personalHub.items.length > 0 ? (
          <YStack mt={isMobile ? '$1' : '$3'} mb="$3" w="100%">
            <CoursesPersonalHub model={personalHub} onOpenSurvey={openSurvey} />
          </YStack>
        ) : null}

        <YStack mb={isSearchActive ? '$2' : '$4'} zIndex={20}>
          <CoursesHubSearch
            tierPick={tierPick}
            onTierPickChange={setTierPick}
            instrumentPick={instrumentPick}
            onInstrumentPickChange={setInstrumentPick}
            autoFocus={false}
            onQueryChange={setSearchQuery}
            onResultsChange={setHubSearch}
          />
        </YStack>

        {isSearchActive ? (
          <YStack mt="$2" mb="$3" gap="$1">
            <SectionTitle color={BRAND_COLORS.text}>
              {hubSearch ? `Search results for "${hubSearch.query}"` : 'Search results'}
            </SectionTitle>
            <CaptionText color={BRAND_COLORS.muted}>
              {displayedCourses.length === 0
                ? 'No courses found.'
                : `${displayedCourses.length} ${displayedCourses.length === 1 ? 'course' : 'courses'}`}
            </CaptionText>
          </YStack>
        ) : (
          <>
            <XStack ai="center" jc="space-between" mb="$2" flexWrap="wrap" gap="$2">
              <XStack ai="baseline" gap="$2" flexShrink={1} minWidth={0}>
                <SectionTitle color={BRAND_COLORS.text}>
                  All courses
                </SectionTitle>
                <CaptionText color={BRAND_COLORS.muted}>
                  {filteredCourses.length}{' '}
                  {filteredCourses.length === 1 ? 'course' : 'courses'}
                </CaptionText>
              </XStack>
              <DashboardViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                gap="$2"
              />
            </XStack>

            <BodyText color={BRAND_COLORS.muted} mb="$3">
              {hasActiveFilters
                ? `Showing ${filteredCourses.length} of ${catalogCourses.length} courses`
                : 'Choose a course to continue learning'}
            </BodyText>

            {filteredCourses.length === 0 ? (
              <YStack ai="center" py="$8" gap="$3">
                <SectionTitle color={BRAND_COLORS.text} ta="center">
                  No courses match these filters
                </SectionTitle>
                <BodyText color={BRAND_COLORS.muted} ta="center">
                  Try a different instrument or skill level.
                </BodyText>
                <Button
                  size="$3"
                  br={30}
                  bg={BRAND_COLORS.accent}
                  color="white"
                  fontWeight="600"
                  onPress={clearFilters}
                  pressStyle={{ opacity: 0.9 }}
                >
                  Clear filters
                </Button>
              </YStack>
            ) : null}
          </>
        )}
      </YStack>
    ),
    [
      isSearchActive,
      personalHub,
      openSurvey,
      isMobile,
      tierPick,
      setTierPick,
      instrumentPick,
      setInstrumentPick,
      hubSearch,
      displayedCourses.length,
      filteredCourses.length,
      hasActiveFilters,
      catalogCourses.length,
      pageWidth,
      clearFilters,
      viewMode,
      setViewMode,
      user?.id,
      trialCta.active,
    ],
  )

  // Pre-chunk into rows so the FlatList always uses a single column. This keeps
  // `numColumns` constant across grid/list, so toggling the view never remounts
  // the list (which would re-mount the header / "For you" hub and cause a blink).
  const courseRows = useMemo<Course[][]>(
    () => chunkIntoRows(displayedCourses, viewMode === 'list' ? 1 : gridColumns),
    [displayedCourses, viewMode, gridColumns],
  )

  const renderItem: ListRenderItem<Course[]> = useCallback(
    ({ item: row }) => {
      if (viewMode === 'list') {
        const course = row[0]
        return (
          <YStack
            width={pageWidth}
            maxWidth={CONTENT_MAX_WIDTH}
            px={CONTAINER_PAD_X}
            als="center"
          >
            <CoursesCatalogRow
              course={course}
              href={`/courses/${course.slug}`}
              thumbnailUri={thumbnailUris[course.id]}
              progressPercentage={(progressByCourseId[course.id] ?? 0) * 100}
              progressBarHeight={progressBarHeight}
              isMobile={isMobile}
              onPress={
                isSearchActive ? () => handleSearchResultPress(course) : undefined
              }
            />
          </YStack>
        )
      }
      return (
        <XStack
          width={gridInnerWidth}
          maxWidth={CONTENT_MAX_WIDTH - CONTAINER_PAD_X * 2}
          als="center"
          gap={GRID_GAP}
          jc={justify === 'center' ? 'center' : 'flex-start'}
        >
          {row.map((course) => (
            <YStack key={course.id} w={cardWidth} style={{ flexGrow: 0, flexShrink: 0 }}>
              <CoursesCatalogCard
                course={course}
                href={`/courses/${course.slug}`}
                width={cardWidth}
                thumbnailUri={thumbnailUris[course.id]}
                progressPercentage={(progressByCourseId[course.id] ?? 0) * 100}
                progressBarHeight={progressBarHeight}
                isMobile={isMobile}
                onPress={
                  isSearchActive ? () => handleSearchResultPress(course) : undefined
                }
              />
            </YStack>
          ))}
        </XStack>
      )
    },
    [
      viewMode,
      pageWidth,
      gridInnerWidth,
      justify,
      cardWidth,
      thumbnailUris,
      progressByCourseId,
      progressBarHeight,
      isMobile,
      isSearchActive,
      handleSearchResultPress,
    ],
  )

  const rowKeyExtractor = useCallback(
    (row: Course[], index: number) =>
      `${viewMode}-${row[0]?.id ?? 'row'}-${index}`,
    [viewMode],
  )

  if (loading) {
    return (
      <YStack f={1} ai="center" jc="center" bg="$background" p="$4">
        <HelpScoutBeacon />
        <CoursePagePremiumLoader variant="coursesList" />
      </YStack>
    )
  }

  if (error) {
    return (
      <YStack f={1} bg="#F8F9FA">
        <ErrorDisplay error={error as Error} onRetry={() => void loadCourses()} />
      </YStack>
    )
  }

  return (
    <YStack f={1} bg="$background">
      <HelpScoutBeacon />
      <FlatList
        ref={listRef}
        style={{ flex: 1, width: '100%', ...scrollbarStyle }}
        data={courseRows}
        keyExtractor={rowKeyExtractor}
        ListHeaderComponent={listHeader}
        ListHeaderComponentStyle={{ zIndex: 30, position: 'relative' }}
        renderItem={renderItem}
        ItemSeparatorComponent={CourseRowSeparator}
        contentContainerStyle={{
          alignItems: 'center',
          paddingBottom: 24,
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        onScroll={handleCatalogScroll}
        scrollEventThrottle={16}
        removeClippedSubviews={Platform.OS !== 'web'}
        initialNumToRender={8}
        maxToRenderPerBatch={8}
        windowSize={5}
      />

      {/* The First Steps trial CTA trumps other announcements while it is showing. */}
      {!trialCta.active ? <CoursesPageAnnouncementModal userId={user?.id} /> : null}
      <FindDashboardSurveyModal
        open={surveyOpen}
        onOpenChange={setSurveyOpen}
      />
    </YStack>
  )
}
